import { createFileRoute, redirect } from '@tanstack/react-router';

import { api } from '@/lib/api/api';
import { navigateTo } from '@/lib/session/navigate';
import { queryClient } from '@/app/query-client';
import { useAuthStore } from '@/lib/session/auth-store';
import { signalLogout } from '@/lib/session/session-isolation';
import { telemetry } from '@/lib/telemetry';

/*
 * Logout (spec §3.10): clear the session store and drop the entire cache — no
 * selective invalidation. Runs in beforeLoad so it happens before any render.
 */
export const Route = createFileRoute('/logout')({
  beforeLoad: async () => {
    // Best-effort server logout; local teardown happens regardless.
    // Identity's logout answers with the hosted provider's logout URL (Cognito):
    // without visiting it the provider's own session survives and the next
    // sign-in silently resumes the same user. Captured here, navigated to after
    // the local teardown.
    const result = await api.auth.logout().catch(() => undefined);
    const logoutUrl = result?.logoutUrl;
    // Cancel any in-flight queries before tearing down so a late resolution
    // cannot repopulate the cache we are about to clear. Awaited (not
    // fire-and-forget) so cancellation completes before the clear — otherwise a
    // query for the signed-out user could settle after clear() and flash their
    // data on the login screen of a shared machine.
    await queryClient.cancelQueries();
    useAuthStore.getState().clear();
    queryClient.clear();
    // Clear RUM user attribution so a subsequent unauthenticated session on a
    // shared machine is not attributed to the signed-out user (§13).
    telemetry.setUser(null);
    // Signal sibling tabs (cookie session leaves nothing else to observe).
    signalLogout();
    if (logoutUrl) {
      navigateTo(logoutUrl);
      return;
    }
    throw redirect({ to: '/login' });
  },
});
